/* tslint:disable */
/* eslint-disable */
import { Injectable } from '@angular/core';
import { HttpClient, HttpResponse, HttpContext } from '@angular/common/http';
import { BaseService } from '../base-service';
import { ApiConfiguration } from '../api-configuration';
import { StrictHttpResponse } from '../strict-http-response';
import { RequestBuilder } from '../request-builder';
import { Observable } from 'rxjs';
import { map, filter } from 'rxjs/operators';

import { ActivateSchemaRequest } from '../models/activate-schema-request';
import { NlsQuerySchemaDoc } from '../models/nls-query-schema-doc';
import { SchemaActivationResult } from '../models/schema-activation-result';

@Injectable({
  providedIn: 'root',
})
export class NlsService extends BaseService {
  constructor(
    config: ApiConfiguration,
    http: HttpClient
  ) {
    super(config, http);
  }

  /**
   * Path part for operation activateSchema
   */
  static readonly ActivateSchemaPath = '/nls/query-schema';

  /**
   * Activate NLS query schema(s) for one or more tenants.
   *
   * Activates schema versions for specified tenants by updating the Azure OpenAI Assistant's function tools and marking them as active in the database.
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `activateSchema()` instead.
   *
   * This method sends `application/json; charset=utf-8` and handles request body of type `application/json; charset=utf-8`.
   */
  activateSchema$Response(params: {

    /**
     * Request containing assistant ID, prompt, and functionsTool dictionary
     */
    body: ActivateSchemaRequest
  },
  context?: HttpContext

): Observable<StrictHttpResponse<SchemaActivationResult>> {

    const rb = new RequestBuilder(this.rootUrl, NlsService.ActivateSchemaPath, 'put');
    if (params) {
      rb.body(params.body, 'application/json; charset=utf-8');
    }

    return this.http.request(rb.build({
      responseType: 'json',
      accept: 'application/json; charset&#x3D;utf-8',
      context: context
    })).pipe(
      filter((r: any) => r instanceof HttpResponse),
      map((r: HttpResponse<any>) => {
        return r as StrictHttpResponse<SchemaActivationResult>;
      })
    );
  }

  /**
   * Activate NLS query schema(s) for one or more tenants.
   *
   * Activates schema versions for specified tenants by updating the Azure OpenAI Assistant's function tools and marking them as active in the database.
   *
   * This method provides access only to the response body.
   * To access the full response (for headers, for example), `activateSchema$Response()` instead.
   *
   * This method sends `application/json; charset=utf-8` and handles request body of type `application/json; charset=utf-8`.
   */
  activateSchema(params: {

    /**
     * Request containing assistant ID, prompt, and functionsTool dictionary
     */
    body: ActivateSchemaRequest
  },
  context?: HttpContext

): Observable<SchemaActivationResult> {

    return this.activateSchema$Response(params,context).pipe(
      map((r: StrictHttpResponse<SchemaActivationResult>) => r.body as SchemaActivationResult)
    );
  }

  /**
   * Path part for operation generateQuerySchema
   */
  static readonly GenerateQuerySchemaPath = '/nls/query-schema';

  /**
   * Generate query schema from NLS template.
   *
   * Fetches the 'nls-query-schema-template' from database, processes it with tenant-specific data, and saves the generated schema to nlsQuerySchemas collection.
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `generateQuerySchema()` instead.
   *
   * This method doesn't expect any request body.
   */
  generateQuerySchema$Response(params: {

    /**
     * The tenant code
     */
    'x-tenant-id': string;
  },
  context?: HttpContext

): Observable<StrictHttpResponse<NlsQuerySchemaDoc>> {

    const rb = new RequestBuilder(this.rootUrl, NlsService.GenerateQuerySchemaPath, 'post');
    if (params) {
      rb.header('x-tenant-id', params['x-tenant-id'], {});
    }

    return this.http.request(rb.build({
      responseType: 'json',
      accept: 'application/json; charset&#x3D;utf-8',
      context: context
    })).pipe(
      filter((r: any) => r instanceof HttpResponse),
      map((r: HttpResponse<any>) => {
        return r as StrictHttpResponse<NlsQuerySchemaDoc>;
      })
    );
  }

  /**
   * Generate query schema from NLS template.
   *
   * Fetches the 'nls-query-schema-template' from database, processes it with tenant-specific data, and saves the generated schema to nlsQuerySchemas collection.
   *
   * This method provides access only to the response body.
   * To access the full response (for headers, for example), `generateQuerySchema$Response()` instead.
   *
   * This method doesn't expect any request body.
   */
  generateQuerySchema(params: {

    /**
     * The tenant code
     */
    'x-tenant-id': string;
  },
  context?: HttpContext

): Observable<NlsQuerySchemaDoc> {

    return this.generateQuerySchema$Response(params,context).pipe(
      map((r: StrictHttpResponse<NlsQuerySchemaDoc>) => r.body as NlsQuerySchemaDoc)
    );
  }

}
